import type { stacks } from "@dropbox/api-v2-client";
import { Button } from "@dropbox/dig-components/buttons";
import { Popover } from "@dropbox/dig-components/popover";
import { Tabs } from "@dropbox/dig-components/tabs";
import { UIIcon } from "@dropbox/dig-icons";
import { EditLine } from "@dropbox/dig-icons/assets";
import { PAP_Update_StackIconColor } from "@mirage/analytics/events/types/update_stack_icon_color";
import i18n from "@mirage/translations";
import classNames from "classnames";
import { atom, useAtom } from "jotai";
import { useRef, useState } from "react";
import { ColorPicker } from "../ColorPicker";
import { HexColorPicker } from "../ColorPicker/HexColorPicker";
import { StackAction } from "../constants";
import { CustomColorPicker } from "../CustomColorPicker";
import {
  useUpdateStackBanner,
  useUpdateStackColorIndex,
  useUpdateStackWithCustomColor,
} from "../hooks";
import { useReportStackPapEvent } from "../hooks/useReportStackPapEvent";
import { getDefaultColorIndex } from "../themes";
import styles from "./StackBannerEditor.module.css";
import { StackUploadPanel } from "./StackUploadPanel";

const actionSurfaceComponent = "stack_header";

const selectedTabAtom = atom<"theme" | "upload">("upload");

export const StackBannerEditor = ({
  stack,
}: {
  stack: stacks.Stack | null;
}) => {
  const reportStackPapEvent = useReportStackPapEvent();
  const [isUploading, setIsUploading] = useState(false);
  const updateStackBanner = useUpdateStackBanner({
    stack,
    setIsUploading,
    isUploading,
  });
  const onColorIndexChange = useUpdateStackColorIndex(stack);
  const onCustomColorChange = useUpdateStackWithCustomColor(stack);
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);

  const editBannerButtonRef = useRef<HTMLButtonElement>(null);
  const [selectedTab, setSelectedTab] = useAtom(selectedTabAtom);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleUpdateStackCustomColor = (customHexColor: string) => {
    onCustomColorChange(customHexColor);
    setIsColorPickerOpen(false);
  };

  const handleUpdateStackColorIndex = (colorIndex: number) => {
    reportStackPapEvent(
      PAP_Update_StackIconColor({
        actionSurfaceComponent,
        stackIconUpdate: "regular",
      }),
    );
    onColorIndexChange(colorIndex);
  };

  return (
    <div className={styles.uploadBannerButtonContainer}>
      <Button
        withIconStart={<UIIcon src={EditLine} />}
        variant="borderless"
        className={classNames(styles.editBannerButton, {
          [styles.editBannerButtonVisible]: isPopoverOpen,
        })}
        onClick={() => setIsPopoverOpen(true)}
        ref={editBannerButtonRef}
      >
        {i18n.t("stack_banner_edit_button")}
      </Button>
      <Popover
        open={isPopoverOpen}
        closeOnOutsideClick
        onToggle={(open: boolean) => {
          setIsPopoverOpen(open);
        }}
        anchorEl={editBannerButtonRef.current}
        placement="bottom-end"
      >
        <Popover.Overlay>
          <Popover.Content>
            <Popover.Body>
              <Tabs
                selectedTab={selectedTab}
                onSelection={(tab) => setSelectedTab(tab as "theme" | "upload")}
              >
                <Tabs.Group variant="responsive">
                  <Tabs.Tab id="theme">
                    {i18n.t("stack_icon_theme_tab")}
                  </Tabs.Tab>
                  <Tabs.Tab id="upload">
                    {i18n.t("stack_icon_upload_tab")}
                  </Tabs.Tab>
                </Tabs.Group>
                <br />
                <Tabs.Panel tabId="theme" className={styles.themePanel}>
                  <ColorPicker
                    colorIndex={
                      stack?.stack_data?.color_index || getDefaultColorIndex()
                    }
                    menuKey={StackAction.COLOR}
                    onSelect={handleUpdateStackColorIndex}
                    stackNamespaceId={stack?.namespace_id}
                  />
                  <CustomColorPicker
                    colorIndex={
                      stack?.stack_data?.color_index || getDefaultColorIndex()
                    }
                    customHexColor={stack?.stack_data?.custom_color}
                    menuKey={StackAction.CUSTOM_COLOR}
                    onSelect={(value) =>
                      value
                        ? handleUpdateStackCustomColor(value)
                        : setIsColorPickerOpen(true)
                    }
                    stackNamespaceId={stack?.namespace_id}
                  />
                  <HexColorPicker
                    anchorRef={editBannerButtonRef}
                    color={stack?.stack_data?.custom_color}
                    isPortaled
                    onClose={() => setIsColorPickerOpen(false)}
                    onSave={handleUpdateStackCustomColor}
                    open={isColorPickerOpen}
                  />
                </Tabs.Panel>
                <Tabs.Panel tabId="upload">
                  <StackUploadPanel
                    upload={updateStackBanner}
                    isUploading={isUploading}
                  />
                </Tabs.Panel>
              </Tabs>
            </Popover.Body>
          </Popover.Content>
        </Popover.Overlay>
      </Popover>
    </div>
  );
};
